import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection, addDoc, getDocs, deleteDoc, doc, updateDoc,
  query, where, serverTimestamp, getDoc, setDoc, orderBy,
} from "firebase/firestore";
import ExcelJS from "exceljs";
import { smartSortFiles } from "./utils/fileOrder";
import { saveAs } from "file-saver";

/* =========================================================================
   Faculty Classroom
   - Each faculty creates their own classes (e.g., "PUC-I Section A")
   - Adds students by email
   - Per class: Reading materials · Quizzes · Simulations
   - Reuses existing Firestore collections (quizzes, files) tagged by classId
   ========================================================================= */

export const SIM_CATALOG = [
  { id: "projectile",         title: "🚀 Projectile Motion",       url: "/simulations/plugplay/projectile.html" },
  { id: "newtons2nd",         title: "⚗️ Newton's 2nd Law",         url: "/simulations/plugplay/newtons2nd.html" },
  { id: "pendulum",           title: "🕰️ Simple Pendulum",          url: "/simulations/plugplay/pendulum.html" },
  { id: "hookes",             title: "🌀 Hooke's Law",              url: "/simulations/plugplay/hookes.html" },
  { id: "friction",           title: "🧱 Friction",                  url: "/simulations/plugplay/friction.html" },
  { id: "elasticity",         title: "🔩 Elasticity",                url: "/simulations/plugplay/elasticity.html" },
  { id: "diffraction",        title: "🌈 Diffraction",               url: "/simulations/plugplay/diffraction.html" },
  { id: "interference",       title: "🔵 Interference",              url: "/simulations/plugplay/interference.html" },
  { id: "wave_optics",        title: "🌊 Wave Optics",               url: "/simulations/plugplay/wave_optics.html" },
  { id: "thermodynamics",     title: "♨️ Thermodynamics",            url: "/simulations/plugplay/thermodynamics.html" },
  { id: "calorimetry",        title: "🔥 Calorimetry",                url: "/simulations/plugplay/calorimetry.html" },
  { id: "pressure_fluids",    title: "💧 Pressure & Fluids",         url: "/simulations/plugplay/pressure_fluids.html" },
  { id: "shm",                title: "📈 Simple Harmonic Motion",    url: "/simulations/plugplay/shm.html" },
  { id: "moment_of_inertia",  title: "🌀 Moment of Inertia",         url: "/simulations/plugplay/moment_of_inertia.html" },
  { id: "circular_motion",    title: "🔄 Circular Motion",           url: "/simulations/plugplay/circular_motion.html" },
  { id: "electric_field",     title: "⚡ Electric Field",            url: "/simulations/plugplay/electric_field_simulator.html" },
];

export default function FacultyClassroom({ user, isAdmin }) {
  const [view, setView] = useState("list");   // "list" | "detail"
  const [classes, setClasses] = useState([]);
  const [activeClass, setActiveClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (user?.email) loadClasses();
  }, [user?.email]);

  const loadClasses = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "facultyClasses"), where("facultyEmail", "==", user.email));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setClasses(list);
    } catch (e) {
      console.error("Failed to load classes", e);
    } finally {
      setLoading(false);
    }
  };

  const openClass = (cls) => { setActiveClass(cls); setView("detail"); };
  const backToList = () => { setActiveClass(null); setView("list"); loadClasses(); };

  if (view === "detail" && activeClass) {
    return <FacultyClassDetail cls={activeClass} user={user} isAdmin={isAdmin} onBack={backToList} onUpdated={loadClasses} />;
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 16px 40px" }}>
      <div style={{
        background: "linear-gradient(135deg,#1a237e 0%,#3949ab 100%)",
        borderRadius: 16, padding: "24px 28px", marginBottom: 24, color: "#fff",
        boxShadow: "0 4px 20px rgba(26,35,126,0.25)"
      }}>
        <h1 style={{ margin: "0 0 4px", fontSize: "1.6rem", fontWeight: 800 }}>📚 My Classroom</h1>
        <p style={{ margin: 0, color: "#ffffff", fontWeight: 600, fontSize: "0.92rem" }}>
          Create your sections, add students, and run reading · quizzes · simulations for just your class.
        </p>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h2 style={{ margin: 0, color: "#1a237e", fontSize: "1.15rem" }}>
          Your Classes <span style={{ color: "#1a237e", fontWeight: 600, fontSize: "0.85rem", fontWeight: 400 }}>({classes.length})</span>
        </h2>
        <button onClick={() => setShowCreate(true)} style={{
          background: "#1a237e", color: "#fff", border: "none", borderRadius: 10,
          padding: "10px 18px", fontWeight: 700, cursor: "pointer", fontSize: "0.9rem"
        }}>＋ Create New Class</button>
      </div>

      {loading ? (
        <p style={{ color: "#1a237e", fontWeight: 600 }}>⏳ Loading…</p>
      ) : classes.length === 0 ? (
        <div style={{ background: "#f5f7ff", borderRadius: 12, padding: "30px 20px", textAlign: "center", color: "#1c2833", fontWeight: 500 }}>
          <div style={{ fontSize: "2.4rem", marginBottom: 8 }}>🏫</div>
          <p style={{ margin: "0 0 6px", fontWeight: 600, color: "#1a237e" }}>No classes yet</p>
          <p style={{ margin: 0, fontSize: "0.88rem" }}>Click <strong>＋ Create New Class</strong> above to set up your first one.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 14 }}>
          {classes.map(c => (
            <div key={c.id} onClick={() => openClass(c)} style={{
              background: "#fff", borderRadius: 14, padding: "18px 18px 14px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.07)", border: "1px solid #e8eaf6",
              cursor: "pointer", transition: "transform .12s,box-shadow .12s"
            }}
            onMouseOver={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 6px 18px rgba(26,35,126,0.15)"; }}
            onMouseOut={e =>  { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.07)"; }}
            >
              <div style={{ fontSize: "1.8rem" }}>{c.icon || "📘"}</div>
              <h3 style={{ margin: "6px 0 4px", color: "#1a237e", fontSize: "1.02rem" }}>{c.name}</h3>
              <p style={{ margin: "0 0 8px", color: "#1c2833", fontWeight: 500, fontSize: "0.82rem" }}>{c.subject || "Physics"}</p>
              <div style={{ display: "flex", gap: 10, color: "#263238", fontWeight: 500, fontSize: "0.78rem" }}>
                <span>👥 {(c.studentEmails || []).length} students</span>
                <span>•</span>
                <span>📝 {c.quizCount || 0} quizzes</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateClassModal user={user} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadClasses(); }} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- */
function CreateClassModal({ user, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("Physics");
  const [icon, setIcon] = useState("📘");
  const [batch, setBatch] = useState("");      // "" | "C1" | "C2" | "Both"
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) { alert("Class name is required"); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, "facultyClasses"), {
        facultyEmail: user.email,
        facultyName: user.displayName || user.email,
        name: name.trim(),
        subject: subject.trim() || "Physics",
        icon: icon || "📘",
        batch: batch || null,            // C1 | C2 | Both | null
        studentEmails: [],
        scheduleSlots: 7,
        periodTimes: {
          1: "9:00 – 10:00",
          2: "10:00 – 11:00",
          3: "11:00 – 12:00",
          4: "1:30 – 2:30",
          5: "2:30 – 3:30",
          6: "3:30 – 4:30",
          7: "4:30 – 5:30",
        },
        createdAt: serverTimestamp(),
      });
      onCreated();
    } catch (e) {
      alert("❌ Create failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <h2 style={{ margin: "0 0 12px", color: "#1a237e" }}>＋ Create New Class</h2>
      <Field label="Class name">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. PUC-I Section A, 2026" autoFocus style={inputStyle} />
      </Field>
      <Field label="Subject">
        <input value={subject} onChange={e => setSubject(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Auto-enroll batch (optional)">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            { val: "",     lbl: "None — manual only" },
            { val: "C1",   lbl: "C1 — all C1 students" },
            { val: "C2",   lbl: "C2 — all C2 students" },
            { val: "Both", lbl: "Both — every student" },
          ].map(o => (
            <button key={o.val} type="button" onClick={() => setBatch(o.val)} style={{
              background: batch === o.val ? "#1a237e" : "#f5f7ff",
              color: batch === o.val ? "#fff" : "#1a237e",
              border: "1.5px solid " + (batch === o.val ? "#1a237e" : "#c5cae9"),
              borderRadius: 8, padding: "6px 12px", fontSize: "0.84rem",
              fontWeight: 600, cursor: "pointer"
            }}>{o.lbl}</button>
          ))}
        </div>
        <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "#37474f", fontWeight: 500 }}>
          When set, students of that batch see this class automatically on login — no need to add them to the roster manually.
        </p>
      </Field>
      <Field label="Icon (emoji)">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["📘","📗","📕","📙","🧪","🔬","⚛️","🧲","🎓","🏫"].map(em => (
            <button key={em} onClick={() => setIcon(em)} style={{
              background: icon === em ? "#1a237e" : "#f5f7ff", border: "1.5px solid " + (icon === em ? "#1a237e" : "#c5cae9"),
              borderRadius: 8, padding: "6px 10px", fontSize: "1.1rem", cursor: "pointer"
            }}>{em}</button>
          ))}
        </div>
      </Field>
      <ModalActions>
        <button onClick={onClose} style={btnSecondary}>Cancel</button>
        <button onClick={create} disabled={saving} style={btnPrimary}>{saving ? "⏳ Creating…" : "✓ Create Class"}</button>
      </ModalActions>
    </Overlay>
  );
}

/* ------------------------------------------------------------------- */
function FacultyClassDetail({ cls, user, isAdmin, onBack, onUpdated }) {
  const [tab, setTab] = useState("reading");
  const [students, setStudents] = useState(cls.studentEmails || []);
  const [readings, setReadings] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [pickedSims, setPickedSims] = useState(cls.simulations || []);
  const [broadcastSims, setBroadcastSims] = useState([]);
  const [busy, setBusy] = useState(false);
  const [announcement, setAnnouncement] = useState(cls.announcement || "");
  const [editingAnnouncement, setEditingAnnouncement] = useState(false);
  const [onlineClasses, setOnlineClasses] = useState([]);

  useEffect(() => { loadReadings(); loadAssignments(); loadQuizzes(); loadBroadcastSims(); loadOnlineClasses(); }, [cls.id]);

  const loadOnlineClasses = async () => {
    try {
      // Single-field orderBy only — no composite index needed.
      const q = query(collection(db, "onlineClasses"), orderBy("scheduledTime", "asc"));
      const snapshot = await getDocs(q);
      const now = Date.now();
      let classes = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        // Show LIVE classes, plus UPCOMING ones that have not ended yet
        .filter(c => {
          if (c.isActive) return true;
          if (c.endedAt) return false;
          const start = new Date(c.scheduledTime).getTime();
          const end = start + (Number(c.duration) || 60) * 60000;
          return end >= now;
        });

      // Shared to specific classrooms (classIds) → only those classrooms.
      // Not shared to any classroom yet → fall back to the batch filter.
      const classBatch = cls.batch || cls.level;
      classes = classes.filter(c => {
        if (Array.isArray(c.classIds) && c.classIds.length > 0) return c.classIds.includes(cls.id);
        if (classBatch && classBatch !== "Both") return c.batch === "Both" || c.batch === classBatch;
        return true;
      });

      setOnlineClasses(classes);
    } catch (err) {
      console.error("Error loading online classes:", err);
    }
  };

  const loadFiles = async (sectionMatch) => {
    const lvl = cls.level || cls.batch;
    const queries = [
      getDocs(query(collection(db, "files"), where("facultyClassId", "==", cls.id))),
      getDocs(query(collection(db, "files"), where("broadcastLevel", "==", "All"))),
    ];
    if (lvl) queries.push(getDocs(query(collection(db, "files"), where("broadcastLevel", "==", lvl))));
    const snaps = await Promise.all(queries);
    const map = new Map();
    snaps.forEach(s => s.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() })));
    return smartSortFiles(Array.from(map.values())
      .filter(f => (f.section || "Reading Materials") === sectionMatch));
  };
  const loadReadings    = async () => setReadings(await loadFiles("Reading Materials"));
  const loadAssignments = async () => setAssignments(await loadFiles("Assignments"));
  const loadBroadcastSims = async () => {
    const lvl = cls.level || cls.batch;
    const queries = [getDocs(query(collection(db, "broadcastSimulations"), where("broadcastLevel", "==", "All")))];
    if (lvl) queries.push(getDocs(query(collection(db, "broadcastSimulations"), where("broadcastLevel", "==", lvl))));
    const snaps = await Promise.all(queries);
    const map = new Map();
    snaps.forEach(s => s.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() })));
    setBroadcastSims(Array.from(map.values()));
  };
  const loadQuizzes = async () => {
    const lvl = cls.level || cls.batch;
    const queries = [
      getDocs(query(collection(db, "quizzes"), where("facultyClassId", "==", cls.id))),
      getDocs(query(collection(db, "quizzes"), where("broadcastLevel", "==", "All"))),
    ];
    if (lvl) queries.push(getDocs(query(collection(db, "quizzes"), where("broadcastLevel", "==", lvl))));
    const snaps = await Promise.all(queries);
    const map = new Map();
    snaps.forEach(s => s.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() })));
    setQuizzes(Array.from(map.values()).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
  };

  const saveAnnouncement = async () => {
    setBusy(true);
    try {
      await updateDoc(doc(db, "facultyClasses", cls.id), { announcement: announcement.trim() });
      cls.announcement = announcement.trim();
      setEditingAnnouncement(false);
    } catch (e) {
      alert("❌ Failed to save announcement: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  const updateClass = async (patch) => {
    setBusy(true);
    try {
      await updateDoc(doc(db, "facultyClasses", cls.id), patch);
      Object.assign(cls, patch);
      onUpdated && onUpdated();
    } catch (e) {
      alert("❌ Update failed: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  const hasSchedule = cls.schedule && Object.keys(cls.schedule).length > 0;
  const showSidebar = hasSchedule && tab !== "schedule"; // hide sidebar on the Schedule tab itself (no point in duplicating)
  const accentByTab = { reading: "#0288d1", schedule: "#7b1fa2", quizzes: "#2e7d32", results: "#e65100", simulations: "#6a1b9a", roster: "#c62828", discussion: "#0097a7" };

  return (
    <div style={{ background: "#eef1f8", minHeight: "100vh", padding: "0 0 30px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto", padding: "20px 16px 40px" }}>
      <button onClick={onBack} style={{
        background: "none", border: "1.5px solid #c5cae9", color: "#1a237e",
        borderRadius: 8, padding: "6px 14px", cursor: "pointer", marginBottom: 14, fontWeight: 600
      }}>← My Classes</button>

      <div style={{
        background: "linear-gradient(135deg,#283593 0%,#3949ab 100%)",
        borderRadius: 14, padding: "22px 24px", marginBottom: 16, color: "#fff",
        display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap"
      }}>
        <div style={{ fontSize: "2.6rem" }}>{cls.icon || "📘"}</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1 style={{ margin: "0 0 4px", fontSize: "1.4rem", fontWeight: 800 }}>{cls.name}</h1>
          <p style={{ margin: 0, color: "#ffffff", fontWeight: 600, fontSize: "0.86rem" }}>
            {cls.subject || "Physics"} · 👥 {students.length} students · 📝 {quizzes.length} quizzes · 🔬 {pickedSims.length} simulations
          </p>
        </div>
        <div>
          <label style={{ display: "block", color: "#fff", fontSize: "0.75rem", fontWeight: 600, marginBottom: 4 }}>Auto-enroll batch</label>
          <select
            value={cls.batch || ""}
            onChange={e => updateClass({ batch: e.target.value || null })}
            style={{
              background: "rgba(255,255,255,0.18)", color: "#fff",
              border: "1.5px solid rgba(255,255,255,0.4)", borderRadius: 8,
              padding: "6px 10px", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer"
            }}
          >
            <option value="" style={{ color: "#1a237e" }}>None — manual only</option>
            <option value="C1" style={{ color: "#1a237e" }}>C1 — all C1 students</option>
            <option value="C2" style={{ color: "#1a237e" }}>C2 — all C2 students</option>
            <option value="Both" style={{ color: "#1a237e" }}>Both — every student</option>
          </select>
        </div>
      </div>

      {/* Announcement Banner */}
      {announcement && !editingAnnouncement ? (
        <div style={{
          background: "linear-gradient(135deg, #ff7043 0%, #e64a19 100%)",
          border: "1.5px solid #d84315",
          borderRadius: 12,
          padding: "16px 20px",
          marginBottom: 18,
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <p style={{ margin: "0 0 8px", fontWeight: 700, fontSize: "0.95rem" }}>📢 Announcement</p>
            <p style={{ margin: 0, color: "#fff", fontSize: "0.9rem", lineHeight: 1.5 }}>{announcement}</p>
          </div>
          {user?.email === cls.facultyEmail && (
            <button
              onClick={() => setEditingAnnouncement(true)}
              style={{
                background: "rgba(255,255,255,0.2)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.4)",
                padding: "6px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: "0.8rem",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              ✏️ Edit
            </button>
          )}
        </div>
      ) : null}

      {editingAnnouncement && (
        <div style={{
          background: "#f5f7ff",
          border: "1.5px solid #c5cae9",
          borderRadius: 12,
          padding: "16px 20px",
          marginBottom: 18,
        }}>
          <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 700, color: "#1a237e", marginBottom: 8 }}>
            📢 Class Announcement (e.g., direct students to online classes)
          </label>
          <textarea
            value={announcement}
            onChange={(e) => setAnnouncement(e.target.value)}
            placeholder="e.g., 🎓 Join Online Class Today: Class 34 Fluids - 3:00 PM - Meeting ID: 627-1798-956"
            style={{
              width: "100%",
              minHeight: "80px",
              padding: "10px",
              borderRadius: 6,
              border: "1px solid #c5cae9",
              fontFamily: "inherit",
              fontSize: "0.9rem",
              marginBottom: 12,
              color: "#1a237e",
            }}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={() => {
                setEditingAnnouncement(false);
                setAnnouncement(cls.announcement || "");
              }}
              style={{
                background: "#e0e0e0",
                color: "#1a237e",
                border: "none",
                padding: "8px 16px",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "0.85rem",
              }}
            >
              Cancel
            </button>
            <button
              onClick={saveAnnouncement}
              disabled={busy}
              style={{
                background: "#1a237e",
                color: "#fff",
                border: "none",
                padding: "8px 16px",
                borderRadius: 6,
                cursor: busy ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: "0.85rem",
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? "⏳ Saving..." : "✅ Save Announcement"}
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e8eaf6", marginBottom: 18, flexWrap: "wrap" }}>
        {[
          { id: "reading",     label: "📖 Reading Materials" },
          { id: "assignments", label: "📋 Assignments" },
          { id: "schedule",    label: "📅 Schedule" },
          { id: "onlineclass", label: "🎓 Online Class" },
          { id: "quizzes",     label: "📝 Quizzes" },
          { id: "results",     label: "📊 Results" },
          { id: "discussion",  label: "💬 Discussion" },
          { id: "simulations", label: "🔬 Simulations" },
          { id: "roster",      label: "👥 Roster" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: tab === t.id ? "#1a237e" : "transparent",
            color: tab === t.id ? "#fff" : "#1a237e",
            border: "none", padding: "10px 16px", fontSize: "0.88rem",
            borderRadius: "8px 8px 0 0", cursor: "pointer", fontWeight: 700,
            borderBottom: tab === t.id ? "2px solid #1a237e" : "2px solid transparent",
            marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Two-column layout: main tab content + schedule side panel */}
      <div style={{
        display: "grid",
        gridTemplateColumns: showSidebar ? "minmax(0, 1fr) 320px" : "1fr",
        gap: 18,
        alignItems: "start",
      }}>
        <div style={{ minWidth: 0 }}>
          {tab === "reading"     && <TabReading cls={cls} readings={readings} onReload={loadReadings} user={user} />}
          {tab === "assignments" && <TabAssignments cls={cls} items={assignments} onReload={loadAssignments} user={user} />}
          {tab === "schedule"    && <TabSchedule cls={cls} editable={true} onSave={(sch) => updateClass(sch)} saving={busy} />}
          {tab === "onlineclass" && <TabOnlineClasses onlineClasses={onlineClasses} />}
          {tab === "quizzes"     && <TabQuizzes cls={cls} quizzes={quizzes} onReload={loadQuizzes} user={user} />}
          {tab === "results"     && <TabResults cls={cls} quizzes={quizzes} students={students} />}
          {tab === "discussion"  && <TabDiscussion cls={cls} user={user} isFacultyOrAdmin={true} />}
          {tab === "simulations" && <TabSimulations cls={cls} picked={pickedSims} setPicked={setPickedSims} broadcastSims={broadcastSims} saving={busy}
                                                    onSave={() => updateClass({ simulations: pickedSims })} />}
          {tab === "roster"      && <TabRoster cls={cls} students={students} setStudents={setStudents} saving={busy}
                                               onSave={() => updateClass({ studentEmails: students })} />}
        </div>

        {showSidebar && (
          <SchedulePanel cls={cls} onEdit={() => setTab("schedule")} />
        )}
      </div>
    </div>
    </div>
  );
}

/* ----- Tabs ----- */
function TabReading({ cls, readings, onReload, user }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [saving, setSaving] = useState(false);

  // ── Share from course library (Engg. Physics / PUC-I / PUC-II pages) ──
  const [showLib, setShowLib] = useState(false);
  const [libSource, setLibSource] = useState("Engineering Physics");
  const [libItems, setLibItems] = useState([]);
  const [libPicked, setLibPicked] = useState({});
  const [libLoading, setLibLoading] = useState(false);
  const [libSharing, setLibSharing] = useState(false);

  const loadLibrary = async (source) => {
    setLibLoading(true); setLibPicked({});
    try {
      const snap = await getDocs(query(collection(db, "files"), where("class", "==", source)));
      const items = smartSortFiles(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(f => (f.section || "Reading Materials") === "Reading Materials"));
      setLibItems(items);
    } catch (e) { alert("❌ Could not load library: " + e.message); }
    finally { setLibLoading(false); }
  };
  useEffect(() => { if (showLib) loadLibrary(libSource); }, [showLib, libSource]);

  const alreadyShared = (f) => readings.some(r => r.url === f.url);
  const shareSelected = async () => {
    const chosen = libItems.filter(f => libPicked[f.id] && !alreadyShared(f));
    if (chosen.length === 0) { alert("Select at least one material that is not already shared."); return; }
    setLibSharing(true);
    try {
      await Promise.all(chosen.map(f => addDoc(collection(db, "files"), {
        facultyClassId: cls.id, name: f.name, url: f.url,
        section: "Reading Materials",
        ...(typeof f.order === "number" ? { order: f.order } : {}),
        sharedFrom: { class: libSource, fileId: f.id },
        addedBy: user.email, createdAt: serverTimestamp(),
      })));
      setLibPicked({}); setShowLib(false); onReload();
      alert(`✅ Shared ${chosen.length} material(s) with this class.`);
    } catch (e) { alert("❌ " + e.message); }
    finally { setLibSharing(false); }
  };

  const add = async () => {
    if (!name.trim() || !url.trim()) { alert("Name and URL are required"); return; }
    setAdding(true);
    try {
      await addDoc(collection(db, "files"), {
        facultyClassId: cls.id, name: name.trim(), url: url.trim(),
        section: "Reading Materials",
        addedBy: user.email, createdAt: serverTimestamp(),
      });
      setName(""); setUrl(""); onReload();
    } catch (e) { alert("❌ " + e.message); }
    finally { setAdding(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this reading?")) return;
    await deleteDoc(doc(db, "files", id)); onReload();
  };
  const startEdit = (r) => { setEditId(r.id); setEditName(r.name); setEditUrl(r.url); };
  const cancelEdit = () => { setEditId(null); setEditName(""); setEditUrl(""); };
  const saveEdit = async () => {
    if (!editName.trim() || !editUrl.trim()) { alert("Name and URL are required"); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, "files", editId), { name: editName.trim(), url: editUrl.trim() });
      cancelEdit(); onReload();
    } catch (e) { alert("❌ " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <Card accent="#0288d1">
      <h3 style={{ ...cardTitle, color: "#01579b", borderBottomColor: "#b3e5fc" }}>📖 Reading Materials</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 8, marginBottom: 14 }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Material name" style={inputStyle} />
        <input value={url}  onChange={e => setUrl(e.target.value)}  placeholder="Link (PDF / Google Drive / YouTube …)" style={inputStyle} />
        <button onClick={add} disabled={adding} style={btnPrimary}>{adding ? "…" : "＋ Add"}</button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <button onClick={() => setShowLib(v => !v)} style={btnSecondary}>
          {showLib ? "✕ Close library" : "📥 Share from course library"}
        </button>
      </div>
      {showLib && (
        <div style={{ border: "1.5px solid #b3e5fc", background: "#f3fbff", borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
            <span style={{ fontWeight: 700, color: "#01579b" }}>Source:</span>
            <select value={libSource} onChange={e => setLibSource(e.target.value)} style={{ ...inputStyle, width: "auto" }}>
              <option value="Engineering Physics">Engg. Physics</option>
              <option value="PUC-I">PUC-I</option>
              <option value="PUC-II">PUC-II</option>
            </select>
            {libItems.length > 0 && (
              <button
                onClick={() => { const all = {}; libItems.forEach(f => { if (!alreadyShared(f)) all[f.id] = true; }); setLibPicked(all); }}
                style={btnSecondary}>Select all</button>
            )}
          </div>
          {libLoading ? <div style={{ color: "#555" }}>Loading…</div>
            : libItems.length === 0 ? <div style={{ color: "#555" }}>No reading materials found in this course.</div>
            : libItems.map(f => (
              <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 4px", color: alreadyShared(f) ? "#999" : "#1a237e", cursor: alreadyShared(f) ? "default" : "pointer" }}>
                <input type="checkbox" disabled={alreadyShared(f)}
                  checked={alreadyShared(f) || !!libPicked[f.id]}
                  onChange={e => setLibPicked(p => ({ ...p, [f.id]: e.target.checked }))} />
                📄 {f.name}{alreadyShared(f) && <span style={{ fontSize: "0.75rem" }}> — already shared</span>}
              </label>
            ))}
          {libItems.length > 0 && (
            <button onClick={shareSelected} disabled={libSharing} style={{ ...btnPrimary, marginTop: 10 }}>
              {libSharing ? "Sharing…" : `Share selected (${Object.values(libPicked).filter(Boolean).length}) with this class`}
            </button>
          )}
        </div>
      )}

      {readings.length === 0 ? <Empty hint="Add a PDF link, drive link, or any reference for your students." />
        : readings.map(r => (
          <Row key={r.id}>
            {editId === r.id ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto auto", gap: 8, width: "100%" }}>
                <input value={editName} onChange={e => setEditName(e.target.value)} style={inputStyle} />
                <input value={editUrl}  onChange={e => setEditUrl(e.target.value)}  style={inputStyle} />
                <button onClick={saveEdit} disabled={saving} style={btnPrimary}>{saving ? "…" : "Save"}</button>
                <button onClick={cancelEdit} style={btnSecondary}>Cancel</button>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <a href={r.url} target="_blank" rel="noreferrer" style={{ color: "#1a237e", textDecoration: "none", fontWeight: 600 }}>📄 {r.name}</a>
                  {r.broadcastLevel && (
                    <span style={{ background: "#fff3e0", color: "#e65100", borderRadius: 10, padding: "1px 8px", fontSize: "0.7rem", fontWeight: 700 }}>📡 {r.broadcastLevel}</span>
                  )}
                </div>
                {!r.broadcastLevel && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => startEdit(r)} style={btnSecondary}>✏️ Edit</button>
                    <button onClick={() => remove(r.id)} style={btnDanger}>Remove</button>
                  </div>
                )}
              </>
            )}
          </Row>
        ))}
    </Card>
  );
}

/* ───────────────────────── Assignments ───────────────────────── */
function TabAssignments({ cls, items, onReload, user }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [editDueDate, setEditDueDate] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!name.trim() || !url.trim()) { alert("Title and URL are required"); return; }
    setAdding(true);
    try {
      await addDoc(collection(db, "files"), {
        facultyClassId: cls.id, name: name.trim(), url: url.trim(),
        section: "Assignments",
        dueDate: dueDate || null,
        addedBy: user.email, createdAt: serverTimestamp(),
      });
      setName(""); setUrl(""); setDueDate(""); onReload();
    } catch (e) { alert("❌ " + e.message); }
    finally { setAdding(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this assignment?")) return;
    await deleteDoc(doc(db, "files", id)); onReload();
  };
  const startEdit = (r) => { setEditId(r.id); setEditName(r.name); setEditUrl(r.url); setEditDueDate(r.dueDate || ""); };
  const cancelEdit = () => { setEditId(null); setEditName(""); setEditUrl(""); setEditDueDate(""); };
  const saveEdit = async () => {
    if (!editName.trim() || !editUrl.trim()) { alert("Title and URL are required"); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, "files", editId), { name: editName.trim(), url: editUrl.trim(), dueDate: editDueDate || null });
      cancelEdit(); onReload();
    } catch (e) { alert("❌ " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <Card accent="#e65100">
      <h3 style={{ ...cardTitle, color: "#bf360c", borderBottomColor: "#ffccbc" }}>📋 Assignments</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 2fr 160px auto", gap: 8, marginBottom: 14 }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Assignment title" style={inputStyle} />
        <input value={url}  onChange={e => setUrl(e.target.value)}  placeholder="Link (PDF / Drive instructions)" style={inputStyle} />
        <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inputStyle} />
        <button onClick={add} disabled={adding} style={btnPrimary}>{adding ? "…" : "＋ Add"}</button>
      </div>
      {items.length === 0 ? <Empty hint="No assignments yet. Add one above, or wait for admin to broadcast one." />
        : items.map(r => {
          const overdue = r.dueDate && new Date(r.dueDate) < new Date();
          return (
            <Row key={r.id}>
              {editId === r.id ? (
                <div style={{ display: "grid", gridTemplateColumns: "1.2fr 2fr 160px auto auto", gap: 8, width: "100%" }}>
                  <input value={editName} onChange={e => setEditName(e.target.value)} style={inputStyle} />
                  <input value={editUrl}  onChange={e => setEditUrl(e.target.value)}  style={inputStyle} />
                  <input type="date" value={editDueDate} onChange={e => setEditDueDate(e.target.value)} style={inputStyle} />
                  <button onClick={saveEdit} disabled={saving} style={btnPrimary}>{saving ? "…" : "Save"}</button>
                  <button onClick={cancelEdit} style={btnSecondary}>Cancel</button>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
                    <a href={r.url} target="_blank" rel="noreferrer" style={{ color: "#1a237e", textDecoration: "none", fontWeight: 700 }}>📋 {r.name}</a>
                    {r.dueDate && (
                      <span style={{ background: overdue ? "#ffebee" : "#fff3e0", color: overdue ? "#c62828" : "#e65100", borderRadius: 10, padding: "1px 8px", fontSize: "0.74rem", fontWeight: 700 }}>
                        📅 Due {new Date(r.dueDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}{overdue ? " — overdue" : ""}
                      </span>
                    )}
                    {r.broadcastLevel && (
                      <span style={{ background: "#fff3e0", color: "#e65100", borderRadius: 10, padding: "1px 8px", fontSize: "0.7rem", fontWeight: 700 }}>📡 {r.broadcastLevel}</span>
                    )}
                  </div>
                  {!r.broadcastLevel && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => startEdit(r)} style={btnSecondary}>✏️ Edit</button>
                      <button onClick={() => remove(r.id)} style={btnDanger}>Remove</button>
                    </div>
                  )}
                </>
              )}
            </Row>
          );
        })}
    </Card>
  );
}

// Date & time window for a class quiz (same fields as Admin quizzes: openFrom / openUntil)
function QuizScheduleEditor({ q, onSaved }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(q.openFrom || "");
  const [until, setUntil] = useState(q.openUntil || "");
  const [saving, setSaving] = useState(false);
  const fmt = v => v ? new Date(v).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : null;
  const save = async () => {
    if (from && until && new Date(until) <= new Date(from)) { alert("Closing time must be after opening time."); return; }
    setSaving(true);
    try { await updateDoc(doc(db, "quizzes", q.id), { openFrom: from || null, openUntil: until || null }); setOpen(false); onSaved && onSaved(); }
    catch (e) { alert("❌ Could not save: " + e.message); }
    setSaving(false);
  };
  const label = (q.openFrom || q.openUntil)
    ? `📅 ${fmt(q.openFrom) || "now"} → ${fmt(q.openUntil) || "no end"}`
    : "📅 Open anytime (no date set)";
  return (
    <div style={{ marginTop: 6 }}>
      <span style={{ fontSize: "0.8rem", fontWeight: 700, color: (q.openFrom || q.openUntil) ? "#1565c0" : "#e65100" }}>{label}</span>
      <button onClick={() => setOpen(!open)} style={{ marginLeft: 10, background: "#fff", border: "1.5px solid #c5cae9", color: "#1a237e", borderRadius: 8, padding: "3px 10px", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>
        {open ? "Close" : "⏰ Set date & time"}
      </button>
      {open && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", marginTop: 8, background: "#f5f7ff", border: "1px solid #c5cae9", borderRadius: 10, padding: 10 }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#1a237e" }}>Opens<br />
            <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></label>
          <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#1a237e" }}>Closes<br />
            <input type="datetime-local" value={until} onChange={e => setUntil(e.target.value)} style={inputStyle} /></label>
          <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? "Saving…" : "💾 Save"}</button>
          {(from || until) && <button onClick={() => { setFrom(""); setUntil(""); }} style={btnSecondary}>Clear</button>}
        </div>
      )}
    </div>
  );
}

function TabQuizzes({ cls, quizzes, onReload, user }) {
  const [title, setTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [shared, setShared] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [cloning, setCloning] = useState(null);

  const loadShared = async () => {
    setLibLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "quizzes"), where("sharedWithTeachers", "==", true)));
      setShared(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    finally { setLibLoading(false); }
  };

  const openLibrary = () => {
    setShowLibrary(true);
    if (shared.length === 0) loadShared();
  };

  const createQuiz = async () => {
    if (!title.trim()) { alert("Title required"); return; }
    setAdding(true);
    try {
      await addDoc(collection(db, "quizzes"), {
        title: title.trim(),
        class: cls.subject || "Physics",
        facultyClassId: cls.id,
        facultyEmail: user.email,
        createdBy: user.email,
        createdByName: user.displayName || user.email,
        timeLimit: 30,
        questions: [],
        assignedBatch: "Both",
        createdAt: serverTimestamp(),
      });
      setTitle(""); onReload();
      alert("✅ Quiz created. Open the Teacher Dashboard to add questions, then launch.");
    } catch (e) { alert("❌ " + e.message); }
    finally { setAdding(false); }
  };

  const removeQuiz = async (id) => {
    if (!window.confirm("Delete this quiz?")) return;
    await deleteDoc(doc(db, "quizzes", id)); onReload();
  };

  const cloneFromShared = async (sq) => {
    if (!window.confirm(`Clone "${sq.title}" into this class?\n\nA copy will be created with you as the owner — you can then edit, schedule, and launch it for your students.`)) return;
    setCloning(sq.id);
    try {
      const { id, createdAt, updatedAt, openFrom, openUntil, sharedWithTeachers, ...rest } = sq;
      await addDoc(collection(db, "quizzes"), {
        ...rest,
        title: rest.title + " (copy)",
        facultyClassId: cls.id,
        facultyEmail: user.email,
        createdBy: user.email,
        createdByName: user.displayName || user.email,
        clonedFrom: sq.id,
        openFrom: null,
        openUntil: null,
        sharedWithTeachers: false,
        createdAt: serverTimestamp(),
      });
      alert("✅ Cloned! Edit it in your class to schedule or change questions.");
      onReload();
    } catch (e) { alert("❌ Clone failed: " + e.message); }
    finally { setCloning(null); }
  };

  return (
    <Card accent="#2e7d32">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <h3 style={{ ...cardTitle, margin: 0, color: "#1b5e20", borderBottomColor: "#c8e6c9" }}>📝 Quizzes for this class</h3>
        <button onClick={openLibrary} style={btnSecondary}>📥 Shared Library</button>
      </div>
      <p style={{ color: "#1c2833", fontWeight: 500, fontSize: "0.85rem", margin: "0 0 10px" }}>
        Create your own quiz, or pick from quizzes shared by admin in the <strong>📥 Shared Library</strong>.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 14 }}>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Quiz title (e.g., Newton's Laws — Class Test 1)" style={inputStyle} />
        <button onClick={createQuiz} disabled={adding} style={btnPrimary}>{adding ? "…" : "＋ Create Quiz"}</button>
      </div>
      {quizzes.length === 0 ? <Empty hint="No quizzes yet for this class." />
        : quizzes.map(q => (
          <Row key={q.id}>
            <div>
              <div style={{ fontWeight: 700, color: "#1a237e" }}>
                {q.title}
                {q.clonedFrom && <span style={{ marginLeft: 8, fontSize: "0.72rem", background: "#e3f2fd", color: "#1565c0", borderRadius: 10, padding: "1px 8px", fontWeight: 600 }}>🔁 cloned</span>}
              </div>
              <div style={{ color: "#263238", fontWeight: 500, fontSize: "0.78rem" }}>{(q.questions || []).length} questions · {q.timeLimit || 30} min</div>
              <QuizScheduleEditor q={q} onSaved={onReload} />
            </div>
            <button onClick={() => removeQuiz(q.id)} style={btnDanger}>Delete</button>
          </Row>
        ))}

      {showLibrary && (
        <Overlay onClose={() => setShowLibrary(false)}>
          <div style={{ maxHeight: "70vh", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h2 style={{ margin: 0, color: "#1a237e", fontSize: "1.15rem" }}>📥 Shared Quiz Library</h2>
              <button onClick={() => setShowLibrary(false)} style={btnSecondary}>✕ Close</button>
            </div>
            <p style={{ color: "#1c2833", fontWeight: 500, fontSize: "0.85rem", margin: "0 0 12px" }}>
              Quizzes shared by admin. Click <strong>Clone for this class</strong> to make your own editable copy for <strong>{cls.name}</strong>.
            </p>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {libLoading ? <p style={{ color: "#263238", fontWeight: 500 }}>⏳ Loading…</p>
                : shared.length === 0 ? <Empty hint="Admin hasn't shared any quizzes yet." />
                : shared.map(sq => (
                  <div key={sq.id} style={{
                    border: "1px solid #e0e4f3", borderRadius: 10, padding: 12, marginBottom: 8, background: "#fafbff"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontWeight: 700, color: "#1a237e", fontSize: "0.95rem" }}>{sq.title}</div>
                        <div style={{ color: "#263238", fontWeight: 500, fontSize: "0.78rem", marginTop: 3 }}>
                          {(sq.questions || []).length} questions · {sq.timeLimit || 30} min · {sq.class || "—"} · by {sq.createdByName || sq.createdBy}
                        </div>
                      </div>
                      <button onClick={() => cloneFromShared(sq)} disabled={cloning === sq.id} style={btnPrimary}>
                        {cloning === sq.id ? "Cloning…" : "🔁 Clone for this class"}
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </Overlay>
      )}
    </Card>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Discussion Board — students post, faculty/admin reply and moderate.
   ───────────────────────────────────────────────────────────────────── */
function TabDiscussion({ cls, user, isFacultyOrAdmin }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [posting, setPosting] = useState(false);
  const [replyOpen, setReplyOpen] = useState({});  // postId -> bool
  const [replyText, setReplyText] = useState({});
  const [showOnly, setShowOnly] = useState("all"); // faculty filter: all | public | private

  const load = async () => {
    setLoading(true);
    try {
      let docs = [];
      if (isFacultyOrAdmin) {
        // Faculty / admin: read all posts in this class
        const snap = await getDocs(query(collection(db, "facultyClassDiscussion"),
                                         where("facultyClassId", "==", cls.id)));
        docs = snap.docs;
      } else {
        // Student: two queries — public posts + their own private posts
        const [pubSnap, priSnap] = await Promise.all([
          getDocs(query(collection(db, "facultyClassDiscussion"),
                        where("facultyClassId", "==", cls.id),
                        where("visibility", "==", "public"))),
          getDocs(query(collection(db, "facultyClassDiscussion"),
                        where("facultyClassId", "==", cls.id),
                        where("visibility", "==", "private"),
                        where("authorEmail", "==", user.email))),
        ]);
        const map = new Map();
        [...pubSnap.docs, ...priSnap.docs].forEach(d => map.set(d.id, d));
        docs = Array.from(map.values());
      }
      const list = docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setPosts(list);
    } catch (e) { console.error("load posts", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [cls.id]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await addDoc(collection(db, "facultyClassDiscussion"), {
        facultyClassId: cls.id,
        classFacultyEmail: cls.facultyEmail || "",   // denormalized so rules can check
        authorEmail: user.email,
        authorName: user.displayName || user.email,
        authorRole: isFacultyOrAdmin ? "faculty" : "student",
        text: text.trim(),
        visibility: isPrivate ? "private" : "public",
        replies: [],
        timestamp: Date.now(),
        createdAt: serverTimestamp(),
      });
      setText(""); setIsPrivate(false); load();
    } catch (e) { alert("❌ " + e.message); }
    finally { setPosting(false); }
  };

  const removePost = async (id) => {
    if (!window.confirm("Delete this post (and all replies)?")) return;
    await deleteDoc(doc(db, "facultyClassDiscussion", id)); load();
  };

  const reply = async (p) => {
    const txt = (replyText[p.id] || "").trim();
    if (!txt) return;
    const newReply = {
      authorEmail: user.email,
      authorName: user.displayName || user.email,
      authorRole: isFacultyOrAdmin ? "faculty" : "student",
      text: txt,
      timestamp: Date.now(),
    };
    try {
      await updateDoc(doc(db, "facultyClassDiscussion", p.id), { replies: [...(p.replies || []), newReply] });
      setReplyText(prev => ({ ...prev, [p.id]: "" }));
      setReplyOpen(prev => ({ ...prev, [p.id]: false }));
      load();
    } catch (e) { alert("❌ " + e.message); }
  };

  const removeReply = async (p, idx) => {
    if (!window.confirm("Delete this reply?")) return;
    const updated = (p.replies || []).filter((_, i) => i !== idx);
    await updateDoc(doc(db, "facultyClassDiscussion", p.id), { replies: updated });
    load();
  };

  const fmtTime = (t) => {
    if (!t) return "—";
    const d = new Date(t);
    const now = Date.now();
    const min = Math.floor((now - t) / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    if (min < 1440) return `${Math.floor(min / 60)}h ago`;
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  return (
    <Card accent="#0097a7">
      <h3 style={{ ...cardTitle, color: "#006064", borderBottomColor: "#b2ebf2" }}>💬 Discussion Board</h3>
      <p style={{ color: "#1c2833", fontWeight: 500, fontSize: "0.85rem", margin: "0 0 12px" }}>
        {isFacultyOrAdmin
          ? "Students post questions and you reply. Use the 🗑 button to remove irrelevant posts."
          : "Ask your faculty a question — they'll reply here. Your name is visible to everyone."}
      </p>

      {/* New post */}
      <div style={{
        background: isPrivate ? "#fff8e1" : "#e0f7fa",
        border: `1.5px solid ${isPrivate ? "#ffd54f" : "#80deea"}`,
        borderRadius: 10, padding: 12, marginBottom: 16,
        transition: "background .2s,border-color .2s"
      }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={2}
          placeholder={
            isPrivate ? "Type a private message to your faculty (other students won't see this)…"
            : isFacultyOrAdmin ? "Post an announcement or reply to the class…"
            : "Type your question or message…"
          }
          style={{ ...inputStyle, fontFamily: "inherit", marginBottom: 8 }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <span style={{ color: isPrivate ? "#e65100" : "#006064", fontSize: "0.8rem", fontWeight: 600 }}>
              Posting as <strong>{user.displayName || user.email}</strong>
            </span>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: "0.82rem", color: isPrivate ? "#e65100" : "#37474f", fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={e => setIsPrivate(e.target.checked)}
                style={{ width: 16, height: 16, cursor: "pointer" }}
              />
              🔒 Private — only faculty can see
            </label>
          </div>
          <button onClick={post} disabled={posting || !text.trim()} style={{
            ...btnPrimary,
            background: isPrivate ? "#e65100" : "#00838f",
            opacity: (!text.trim() || posting) ? 0.55 : 1
          }}>
            {posting ? "Posting…" : isPrivate ? "🔒 Send Privately" : "📩 Post"}
          </button>
        </div>
      </div>

      {/* Faculty filter */}
      {isFacultyOrAdmin && posts.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          {[
            { id: "all",     lbl: `All (${posts.length})` },
            { id: "private", lbl: `🔒 Private (${posts.filter(p => p.visibility === "private").length})` },
            { id: "public",  lbl: `🌐 Public (${posts.filter(p => p.visibility !== "private").length})` },
          ].map(f => (
            <button key={f.id} onClick={() => setShowOnly(f.id)} style={{
              background: showOnly === f.id ? "#00838f" : "#fff",
              color: showOnly === f.id ? "#fff" : "#00838f",
              border: "1.5px solid #00838f",
              borderRadius: 16, padding: "4px 12px",
              fontWeight: 600, fontSize: "0.78rem", cursor: "pointer"
            }}>{f.lbl}</button>
          ))}
        </div>
      )}

      {/* Posts */}
      {loading ? <p style={{ color: "#263238", fontWeight: 500 }}>⏳ Loading discussion…</p>
        : posts.length === 0 ? <Empty hint="No posts yet — start the conversation!" />
        : posts
            .filter(p => !isFacultyOrAdmin || showOnly === "all"
                            || (showOnly === "private" && p.visibility === "private")
                            || (showOnly === "public"  && p.visibility !== "private"))
            .map(p => {
          const isPriv = p.visibility === "private";
          return (
          <div key={p.id} style={{
            background: isPriv ? "#fffbe6" : "#fff",
            border: `1.5px solid ${isPriv ? "#ffd54f" : "#c5cae9"}`,
            borderLeft: isPriv ? "4px solid #ef6c00"
                       : p.authorRole === "faculty" ? "4px solid #00838f" : "4px solid #1a237e",
            borderRadius: 10, padding: "12px 14px", marginBottom: 12,
            boxShadow: "0 2px 6px rgba(26,35,126,0.06)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div>
                <span style={{ fontWeight: 700, color: "#1a237e", fontSize: "0.92rem" }}>{p.authorName}</span>
                {p.authorRole === "faculty" && (
                  <span style={{ marginLeft: 8, background: "#00838f", color: "#fff", borderRadius: 10, padding: "1px 8px", fontSize: "0.7rem", fontWeight: 700 }}>FACULTY</span>
                )}
                {isPriv && (
                  <span style={{ marginLeft: 8, background: "#ef6c00", color: "#fff", borderRadius: 10, padding: "1px 8px", fontSize: "0.7rem", fontWeight: 700 }}>🔒 PRIVATE</span>
                )}
                <span style={{ marginLeft: 8, color: "#263238", fontWeight: 500, fontSize: "0.78rem" }}>· {fmtTime(p.timestamp)}</span>
              </div>
              {(isFacultyOrAdmin || p.authorEmail === user.email) && (
                <button onClick={() => removePost(p.id)} style={btnDanger}>🗑 Delete</button>
              )}
            </div>
            <p style={{ margin: "8px 0 10px", color: "#1c2833", fontWeight: 500, fontSize: "0.92rem", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
              {p.text}
            </p>

            {/* Replies */}
            {(p.replies || []).length > 0 && (
              <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: "2px solid #e0e4f3" }}>
                {p.replies.map((r, i) => (
                  <div key={i} style={{ background: r.authorRole === "faculty" ? "#e0f7fa" : "#f5f7ff", borderRadius: 8, padding: "8px 12px", marginBottom: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: "0.82rem" }}>
                        <strong style={{ color: r.authorRole === "faculty" ? "#006064" : "#1a237e" }}>{r.authorName}</strong>
                        {r.authorRole === "faculty" && (
                          <span style={{ marginLeft: 6, background: "#00838f", color: "#fff", borderRadius: 8, padding: "0 6px", fontSize: "0.65rem", fontWeight: 700 }}>FACULTY</span>
                        )}
                        <span style={{ marginLeft: 6, color: "#263238", fontWeight: 500, fontSize: "0.74rem" }}>· {fmtTime(r.timestamp)}</span>
                      </span>
                      {(isFacultyOrAdmin || r.authorEmail === user.email) && (
                        <button onClick={() => removeReply(p, i)} style={{ ...btnDanger, padding: "3px 8px", fontSize: "0.72rem" }}>✕</button>
                      )}
                    </div>
                    <p style={{ margin: "4px 0 0", color: "#1c2833", fontWeight: 500, fontSize: "0.86rem", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{r.text}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Reply box */}
            {replyOpen[p.id] ? (
              <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr auto auto", gap: 6 }}>
                <input
                  value={replyText[p.id] || ""}
                  onChange={e => setReplyText(prev => ({ ...prev, [p.id]: e.target.value }))}
                  placeholder="Type your reply…"
                  style={inputStyle}
                  onKeyDown={e => e.key === "Enter" && reply(p)}
                  autoFocus
                />
                <button onClick={() => reply(p)} style={{ ...btnPrimary, background: "#00838f" }}>Send</button>
                <button onClick={() => setReplyOpen(prev => ({ ...prev, [p.id]: false }))} style={btnSecondary}>Cancel</button>
              </div>
            ) : (
              <button onClick={() => setReplyOpen(prev => ({ ...prev, [p.id]: true }))}
                style={{ background: "none", border: "none", color: "#0097a7", fontWeight: 600, fontSize: "0.82rem", cursor: "pointer", padding: 0, marginTop: 4 }}>
                ↩ Reply
              </button>
            )}
          </div>
          );
        })}
    </Card>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Schedule (weekly timetable) — used by both faculty (editable) and
   students (read-only via the `editable` prop).
   ───────────────────────────────────────────────────────────────────── */
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DEFAULT_PERIODS = 7;
const DEFAULT_PERIOD_TIMES = {
  1: "9:00 – 10:00",
  2: "10:00 – 11:00",
  3: "11:00 – 12:00",
  4: "1:30 – 2:30",
  5: "2:30 – 3:30",
  6: "3:30 – 4:30",
  7: "4:30 – 5:30",
};
const SLOT_TYPES = {
  theory: { label: "Theory", short: "T", bg: "#e3f2fd", color: "#1565c0", border: "#90caf9" },
  lab:    { label: "Lab",    short: "L", bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
  tut:    { label: "Tutorial", short: "TT", bg: "#fff3e0", color: "#e65100", border: "#ffcc80" },
};
const TYPE_CYCLE = ["", "theory", "lab", "tut"]; // click to cycle

function TabSchedule({ cls, editable, onSave, saving }) {
  // schedule shape: { Mon: { 1: "theory", 4: "lab" }, Thu: {...}, ... }
  const [schedule, setSchedule] = useState(cls.schedule || {});
  const [periods, setPeriods] = useState(cls.scheduleSlots || DEFAULT_PERIODS);
  const [periodTimes, setPeriodTimes] = useState(
    Object.keys(cls.periodTimes || {}).length > 0 ? cls.periodTimes : { ...DEFAULT_PERIOD_TIMES }
  );
  const [dirty, setDirty] = useState(false);

  const cycleSlot = (day, p) => {
    if (!editable) return;
    setSchedule(prev => {
      const next = { ...prev, [day]: { ...(prev[day] || {}) } };
      const cur = next[day][p] || "";
      const idx = TYPE_CYCLE.indexOf(cur);
      const newVal = TYPE_CYCLE[(idx + 1) % TYPE_CYCLE.length];
      if (newVal) next[day][p] = newVal;
      else delete next[day][p];
      if (Object.keys(next[day]).length === 0) delete next[day];
      return next;
    });
    setDirty(true);
  };

  const setSlotType = (day, p, type) => {
    if (!editable) return;
    setSchedule(prev => {
      const next = { ...prev, [day]: { ...(prev[day] || {}) } };
      if (!type) { delete next[day][p]; if (Object.keys(next[day]).length === 0) delete next[day]; }
      else next[day][p] = type;
      return next;
    });
    setDirty(true);
  };

  const updatePeriodTime = (p, value) => {
    setPeriodTimes(prev => ({ ...prev, [p]: value }));
    setDirty(true);
  };

  const handleSave = () => {
    onSave && onSave({ schedule, scheduleSlots: periods, periodTimes });
    setDirty(false);
  };

  const clearAll = () => {
    if (!window.confirm("Clear the entire schedule for this class?")) return;
    setSchedule({}); setDirty(true);
  };

  return (
    <Card accent="#7b1fa2">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <h3 style={{ ...cardTitle, margin: 0, color: "#4a148c", borderBottomColor: "#e1bee7" }}>📅 Weekly Schedule</h3>
        {editable && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <label style={{ fontSize: "0.82rem", color: "#555", display: "flex", alignItems: "center", gap: 6 }}>
              Periods:
              <select value={periods} onChange={e => { setPeriods(parseInt(e.target.value)); setDirty(true); }}
                style={{ padding: "5px 8px", borderRadius: 6, border: "1.5px solid #c5cae9" }}>
                {[5, 6, 7, 8].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button onClick={clearAll} style={btnDanger}>Clear</button>
            <button onClick={handleSave} disabled={!dirty || saving} style={{ ...btnPrimary, opacity: !dirty ? 0.5 : 1 }}>
              {saving ? "Saving…" : dirty ? "💾 Save Schedule" : "✓ Saved"}
            </button>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14, fontSize: "0.82rem" }}>
        {Object.entries(SLOT_TYPES).map(([key, t]) => (
          <span key={key} style={{
            display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px",
            background: t.bg, color: t.color, borderRadius: 14, border: `1px solid ${t.border}`, fontWeight: 600
          }}>
            <span style={{
              width: 16, height: 16, borderRadius: 3,
              background: t.color, color: "#fff",
              fontSize: "0.7rem", fontWeight: 700,
              display: "inline-flex", alignItems: "center", justifyContent: "center"
            }}>{t.short}</span>
            {t.label}
          </span>
        ))}
        {editable && (
          <span style={{ color: "#263238", fontWeight: 500, fontSize: "0.78rem", marginLeft: "auto" }}>
            Tip: Click a cell to cycle Theory → Lab → Tutorial → empty
          </span>
        )}
      </div>

      {/* Grid */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 4, minWidth: 600 }}>
          <thead>
            <tr>
              <th style={{ background: "#1a237e", color: "#fff", padding: "10px 8px", borderRadius: 6, fontSize: "0.82rem", minWidth: 80 }}>Day / Period</th>
              {Array.from({ length: periods }, (_, i) => i + 1).map(p => (
                <th key={p} style={{ background: "#283593", color: "#fff", padding: "8px 4px", borderRadius: 6, fontSize: "0.82rem", minWidth: 80 }}>
                  <div style={{ fontWeight: 700 }}>P{p}</div>
                  {editable ? (
                    <input
                      value={periodTimes[p] || ""}
                      onChange={e => updatePeriodTime(p, e.target.value)}
                      placeholder="9:30–10:30"
                      style={{
                        width: "100%", padding: "2px 4px", marginTop: 4,
                        background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)",
                        borderRadius: 4, color: "#fff", fontSize: "0.7rem", textAlign: "center"
                      }}
                    />
                  ) : (periodTimes[p] && (
                    <div style={{ fontSize: "0.68rem", color: "#ffffff", fontWeight: 600, marginTop: 2 }}>{periodTimes[p]}</div>
                  ))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DAYS.map(day => (
              <tr key={day}>
                <td style={{ background: "#e8eaf6", color: "#1a237e", padding: "10px 8px", borderRadius: 6, fontWeight: 700, textAlign: "center", fontSize: "0.88rem" }}>
                  {day}
                </td>
                {Array.from({ length: periods }, (_, i) => i + 1).map(p => {
                  const type = (schedule[day] || {})[p] || "";
                  const meta = SLOT_TYPES[type];
                  return (
                    <td key={p} onClick={() => cycleSlot(day, p)} style={{
                      padding: 0, borderRadius: 6, position: "relative",
                      cursor: editable ? "pointer" : "default",
                    }}>
                      <div style={{
                        background: meta ? meta.bg : "#fafbff",
                        color: meta ? meta.color : "#bbb",
                        border: `1.5px solid ${meta ? meta.border : "#e0e4f3"}`,
                        borderRadius: 6, padding: "12px 6px", textAlign: "center",
                        fontWeight: 700, fontSize: "0.88rem",
                        transition: "transform .1s",
                      }}>
                        {meta ? (
                          <>
                            <div style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: 22, height: 22, background: meta.color, color: "#fff",
                              borderRadius: 4, fontSize: "0.75rem", fontWeight: 800
                            }}>{meta.short}</div>
                            {editable && (
                              <select
                                value={type}
                                onClick={e => e.stopPropagation()}
                                onChange={e => setSlotType(day, p, e.target.value)}
                                style={{
                                  width: "100%", marginTop: 4, padding: "2px 4px",
                                  border: `1px solid ${meta.border}`, borderRadius: 4,
                                  fontSize: "0.7rem", color: meta.color, background: "#fff",
                                  cursor: "pointer"
                                }}
                              >
                                <option value="">— remove —</option>
                                {Object.entries(SLOT_TYPES).map(([k, t]) => (
                                  <option key={k} value={k}>{t.label}</option>
                                ))}
                              </select>
                            )}
                          </>
                        ) : (
                          editable ? <span style={{ fontSize: "1.1rem", color: "#bbb" }}>＋</span> : <span>—</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary preview */}
      {Object.keys(schedule).length > 0 && (
        <div style={{ marginTop: 16, background: "#f5f7ff", borderRadius: 8, padding: "10px 14px", fontSize: "0.85rem" }}>
          <div style={{ fontWeight: 700, color: "#1a237e", marginBottom: 4 }}>📋 At a glance</div>
          {DAYS.filter(d => schedule[d] && Object.keys(schedule[d]).length).map(d => {
            const slots = Object.entries(schedule[d]).sort(([a],[b]) => Number(a)-Number(b))
              .map(([p, type]) => `P${p} ${SLOT_TYPES[type]?.short || ""}`).join(", ");
            return <div key={d} style={{ color: "#444", marginBottom: 2 }}>
              <strong style={{ color: "#1a237e" }}>{d}:</strong> {slots}
            </div>;
          })}
        </div>
      )}
    </Card>
  );
}

/* Compact read-only schedule shown as a sticky side panel */
function SchedulePanel({ cls, onEdit }) {
  const schedule = cls.schedule || {};
  const periods = cls.scheduleSlots || DEFAULT_PERIODS;
  const periodTimes = cls.periodTimes || {};
  const filledDays = DAYS.filter(d => schedule[d] && Object.keys(schedule[d]).length);

  return (
    <div style={{
      background: "#fff", borderRadius: 12, padding: 14,
      border: "1px solid #e8eaf6", boxShadow: "0 2px 10px rgba(26,35,126,0.06)",
      position: "sticky", top: 14,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h3 style={{ margin: 0, color: "#1a237e", fontSize: "0.95rem", fontWeight: 800 }}>📅 Class Schedule</h3>
        {onEdit && (
          <button onClick={onEdit} style={{
            background: "#e8eaf6", color: "#1a237e", border: "none",
            borderRadius: 6, padding: "4px 10px", fontSize: "0.75rem",
            fontWeight: 600, cursor: "pointer"
          }}>✎ Edit</button>
        )}
      </div>

      {filledDays.length === 0 ? (
        <p style={{ color: "#1a237e", fontWeight: 600, fontSize: "0.82rem", margin: 0 }}>No periods set yet.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filledDays.map(day => {
            const slots = Object.entries(schedule[day]).sort(([a],[b]) => Number(a)-Number(b));
            return (
              <div key={day} style={{
                background: "#f5f7ff", borderRadius: 8, padding: "8px 10px"
              }}>
                <div style={{ fontWeight: 700, color: "#1a237e", fontSize: "0.82rem", marginBottom: 5 }}>
                  {day}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {slots.map(([p, type]) => {
                    const meta = SLOT_TYPES[type] || { label: type, short: "?", bg: "#eee", color: "#1c2833", fontWeight: 500, border: "#ccc" };
                    return (
                      <div key={p} style={{
                        display: "grid", gridTemplateColumns: "28px 1fr 22px", gap: 6, alignItems: "center"
                      }}>
                        <span style={{
                          background: "#1a237e", color: "#fff", borderRadius: 4,
                          fontSize: "0.7rem", fontWeight: 700, textAlign: "center",
                          padding: "2px 0"
                        }}>P{p}</span>
                        <div style={{ fontSize: "0.78rem", color: "#444", lineHeight: 1.25 }}>
                          <div style={{ fontWeight: 600, color: meta.color }}>{meta.label}</div>
                          {periodTimes[p] && (
                            <div style={{ color: "#263238", fontWeight: 500, fontSize: "0.7rem" }}>{periodTimes[p]}</div>
                          )}
                        </div>
                        <span style={{
                          background: meta.color, color: "#fff", borderRadius: 4,
                          fontSize: "0.68rem", fontWeight: 700, textAlign: "center", padding: "2px 0"
                        }}>{meta.short}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Periods count footer */}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #eef0f9", fontSize: "0.7rem", color: "#263238", fontWeight: 500 }}>
        {periods} periods/day · {filledDays.length}/{DAYS.length} active days
      </div>
    </div>
  );
}

function TabOnlineClasses({ onlineClasses }) {
  return (
    <Card accent="#1e88e5">
      <h3 style={{ ...cardTitle, color: "#0d47a1", borderBottomColor: "#90caf9" }}>🎓 Online Classes</h3>

      {onlineClasses.length === 0 ? (
        <Empty hint="No live or upcoming online classes. Check back later!" />
      ) : (
        <div style={{ display: "grid", gap: "12px" }}>
          {onlineClasses.map((cls) => (
            <div
              key={cls.id}
              style={{
                background: "#e3f2fd",
                border: "1.5px solid #90caf9",
                borderRadius: "10px",
                padding: "14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div style={{ flex: 1, minWidth: "200px" }}>
                <div style={{ fontWeight: 700, color: "#1a237e", marginBottom: "4px", fontSize: "0.95rem" }}>
                  {cls.isActive ? "🔴 LIVE: " : "🕒 UPCOMING: "}{cls.className}
                </div>
                <p style={{ margin: "0 0 4px", color: "#555", fontSize: "0.85rem" }}>
                  📅 {new Date(cls.scheduledTime).toLocaleString("en-IN")}
                </p>
                <p style={{ margin: "0 0 4px", color: "#666", fontSize: "0.8rem" }}>
                  {cls.description}
                </p>
                <p style={{ margin: "0 0 4px", color: "#1e88e5", fontSize: "0.85rem", fontWeight: 600 }}>
                  🔗 Meeting ID: <code style={{ background: "rgba(30,136,229,0.1)", padding: "2px 6px", borderRadius: "4px", fontFamily: "monospace" }}>{cls.roomId}</code>
                </p>
                <p style={{ margin: 0, color: "#666", fontSize: "0.8rem" }}>
                  👥 Batch: {cls.batch || "Both"}
                </p>
              </div>
              <button
                onClick={() => window.open(`https://meet.google.com/${cls.roomId}`, "_blank")}
                style={{
                  background: "#1e88e5",
                  color: "white",
                  border: "none",
                  padding: "10px 18px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                👁️ Join Class
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function TabResults({ cls, quizzes, students }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quizFilter, setQuizFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!quizzes || quizzes.length === 0) { setResults([]); setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        // Firestore "in" only accepts up to 10 values — batch in chunks.
        const allResults = [];
        const ids = quizzes.map(q => q.id);
        for (let i = 0; i < ids.length; i += 10) {
          const chunk = ids.slice(i, i + 10);
          const snap = await getDocs(query(collection(db, "quizResults"), where("quizId", "in", chunk)));
          snap.forEach(d => allResults.push({ id: d.id, ...d.data() }));
        }
        // Sort by most recent first
        allResults.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setResults(allResults);
      } catch (e) { console.error("load results", e); }
      finally { setLoading(false); }
    })();
  }, [cls.id, quizzes]);

  const filtered = results.filter(r => {
    if (quizFilter !== "ALL" && r.quizId !== quizFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.studentName || "").toLowerCase().includes(q) ||
           (r.studentEmail || "").toLowerCase().includes(q) ||
           (r.quizTitle || "").toLowerCase().includes(q);
  });

  // Stats
  const avgPercent = filtered.length === 0 ? 0
    : Math.round(filtered.reduce((s, r) => s + (r.percent || 0), 0) / filtered.length);
  const highest = filtered.length === 0 ? 0
    : Math.max(...filtered.map(r => r.percent || 0));
  const passed = filtered.filter(r => (r.percent || 0) >= 40).length;
  const submittedEmails = new Set(filtered.map(r => (r.studentEmail || "").toLowerCase()));
  const notYetSubmitted = students.filter(e => !submittedEmails.has(e));

  const downloadExcel = async () => {
    if (filtered.length === 0) { alert("No results to export."); return; }
    setExporting(true);
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(cls.name.slice(0, 28));
      ws.columns = [
        { header: "#",            key: "no",      width: 5 },
        { header: "Quiz",         key: "quiz",    width: 32 },
        { header: "Student Name", key: "name",    width: 26 },
        { header: "Email",        key: "email",   width: 32 },
        { header: "Batch",        key: "batch",   width: 8 },
        { header: "Score",        key: "score",   width: 8 },
        { header: "Total",        key: "total",   width: 8 },
        { header: "Percent",      key: "percent", width: 10 },
        { header: "Grade",        key: "grade",   width: 12 },
        { header: "Submitted At", key: "date",    width: 22 },
      ];
      ws.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      ws.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1A237E" } };
      filtered.forEach((r, i) => {
        const grade = r.percent >= 90 ? "Excellent" : r.percent >= 75 ? "Great" : r.percent >= 50 ? "Good" : r.percent >= 40 ? "Pass" : "Fail";
        ws.addRow({
          no: i + 1,
          quiz: r.quizTitle || "",
          name: r.studentName || "",
          email: r.studentEmail || "",
          batch: r.studentBatch || "—",
          score: r.score ?? 0,
          total: r.total ?? 0,
          percent: r.percent ?? 0,
          grade,
          date: r.submittedAt ? new Date(r.submittedAt).toLocaleString("en-IN") : "—",
        });
      });
      // Summary row
      ws.addRow({});
      const sumRow = ws.addRow({ no: "", quiz: "SUMMARY", name: `${filtered.length} submissions`, email: `Avg: ${avgPercent}%`, batch: "", score: "", total: "", percent: `Top: ${highest}%`, grade: `Passed: ${passed}/${filtered.length}`, date: "" });
      sumRow.font = { bold: true };
      sumRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8EAF6" } };

      const buf = await wb.xlsx.writeBuffer();
      const fname = `${cls.name.replace(/[^\w\-]+/g, "_")}_results_${new Date().toISOString().slice(0,10)}.xlsx`;
      saveAs(new Blob([buf], { type: "application/octet-stream" }), fname);
    } catch (e) { alert("❌ Export failed: " + e.message); }
    finally { setExporting(false); }
  };

  return (
    <Card accent="#e65100">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <h3 style={{ ...cardTitle, margin: 0, color: "#bf360c", borderBottomColor: "#ffccbc" }}>📊 Quiz Results</h3>
        <button onClick={downloadExcel} disabled={exporting || filtered.length === 0} style={btnPrimary}>
          {exporting ? "⏳ Exporting…" : "📥 Download Excel"}
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, marginBottom: 14 }}>
        {[
          { label: "Submissions",  value: filtered.length,                    bg: "#e3f2fd", color: "#1565c0" },
          { label: "Class Average",value: filtered.length ? avgPercent + "%" : "—", bg: "#e8f5e9", color: "#2e7d32" },
          { label: "Top Score",    value: filtered.length ? highest + "%" : "—",    bg: "#fff3e0", color: "#e65100" },
          { label: "Passed (≥40%)",value: filtered.length ? `${passed}/${filtered.length}` : "—", bg: "#f3e5f5", color: "#6a1b9a" },
          { label: "Not Submitted",value: notYetSubmitted.length,             bg: "#ffebee", color: "#c62828" },
        ].map((s, i) => (
          <div key={i} style={{ background: s.bg, borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: "0.74rem", color: s.color, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
            <div style={{ fontSize: "1.25rem", color: s.color, fontWeight: 800 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <select value={quizFilter} onChange={e => setQuizFilter(e.target.value)} style={inputStyle}>
          <option value="ALL">All quizzes ({results.length})</option>
          {quizzes.map(q => (
            <option key={q.id} value={q.id}>{q.title}</option>
          ))}
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search student or quiz" style={inputStyle} />
      </div>

      {/* Table */}
      {loading ? <p style={{ color: "#263238", fontWeight: 500 }}>⏳ Loading results…</p>
        : filtered.length === 0 ? <Empty hint="No submissions yet for this filter." />
        : (
          <div style={{ overflowX: "auto", border: "1px solid #e0e4f3", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ background: "#1a237e", color: "#fff" }}>
                  <th style={th}>#</th>
                  <th style={th}>Quiz</th>
                  <th style={th}>Student</th>
                  <th style={th}>Batch</th>
                  <th style={{ ...th, textAlign: "center" }}>Score</th>
                  <th style={{ ...th, textAlign: "center" }}>%</th>
                  <th style={th}>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.id} style={{ background: i % 2 ? "#fafbff" : "#fff" }}>
                    <td style={td}>{i + 1}</td>
                    <td style={td}>{r.quizTitle}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: "#1a237e" }}>{r.studentName}</div>
                      <div style={{ color: "#263238", fontWeight: 500, fontSize: "0.78rem" }}>{r.studentEmail}</div>
                    </td>
                    <td style={td}>{r.studentBatch || "—"}</td>
                    <td style={{ ...td, textAlign: "center", fontWeight: 700 }}>{r.score} / {r.total}</td>
                    <td style={{ ...td, textAlign: "center" }}>
                      <span style={{
                        background: r.percent >= 75 ? "#e8f5e9" : r.percent >= 40 ? "#fff3e0" : "#ffebee",
                        color: r.percent >= 75 ? "#2e7d32" : r.percent >= 40 ? "#e65100" : "#c62828",
                        borderRadius: 12, padding: "3px 10px", fontWeight: 700, fontSize: "0.82rem",
                      }}>{r.percent}%</span>
                    </td>
                    <td style={{ ...td, color: "#1c2833", fontWeight: 500, fontSize: "0.78rem" }}>
                      {r.submittedAt ? new Date(r.submittedAt).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* Not submitted list */}
      {students.length > 0 && notYetSubmitted.length > 0 && quizFilter === "ALL" && (
        <details style={{ marginTop: 14 }}>
          <summary style={{ cursor: "pointer", color: "#c62828", fontWeight: 600, fontSize: "0.86rem" }}>
            ⚠️ {notYetSubmitted.length} student(s) have not submitted any quiz
          </summary>
          <div style={{ marginTop: 8, fontSize: "0.82rem", color: "#1c2833", fontWeight: 500, padding: "6px 12px", background: "#ffebee", borderRadius: 6 }}>
            {notYetSubmitted.join(", ")}
          </div>
        </details>
      )}
    </Card>
  );
}

const th = { textAlign: "left", padding: "10px 12px", fontWeight: 700, fontSize: "0.82rem", borderBottom: "1px solid rgba(255,255,255,0.18)" };
const td = { padding: "9px 12px", borderBottom: "1px solid #f0f2f9", verticalAlign: "top" };

function TabSimulations({ cls, picked, setPicked, onSave, saving, broadcastSims = [] }) {
  const toggle = (id) => setPicked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  return (
    <Card accent="#6a1b9a">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ ...cardTitle, margin: 0, color: "#4a148c", borderBottomColor: "#e1bee7" }}>🔬 Pick simulations for your class</h3>
        <button onClick={onSave} disabled={saving} style={btnPrimary}>{saving ? "Saving…" : "💾 Save Selection"}</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 10 }}>
        {SIM_CATALOG.map(s => {
          const on = picked.includes(s.id);
          return (
            <label key={s.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
              background: on ? "#e8eaf6" : "#fafbff",
              border: `1.5px solid ${on ? "#1a237e" : "#e0e4f3"}`,
              borderRadius: 10, cursor: "pointer"
            }}>
              <input type="checkbox" checked={on} onChange={() => toggle(s.id)} />
              <span style={{ fontWeight: 600, color: on ? "#1a237e" : "#333", fontSize: "0.86rem" }}>{s.title}</span>
            </label>
          );
        })}
      </div>

      {broadcastSims.length > 0 && (
        <>
          <h4 style={{ color: "#4a148c", margin: "20px 0 8px", fontSize: "0.95rem", borderBottom: "2px solid #e1bee7", paddingBottom: 6 }}>
            📡 Admin Broadcast Simulations <span style={{ color: "#546e7a", fontWeight: 500, fontSize: "0.78rem" }}>(automatic — managed in Admin Panel)</span>
          </h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 10 }}>
            {broadcastSims.map(s => (
              <a key={s.id} href={s.url} target="_blank" rel="noreferrer" style={{
                display: "block", padding: "10px 12px", background: "#fff3e0",
                border: "1.5px solid #ffb74d", borderRadius: 10, textDecoration: "none",
                color: "#e65100", fontWeight: 600, fontSize: "0.86rem"
              }}>
                🔬 {s.title}
                <span style={{ display: "block", color: "#bf360c", fontWeight: 600, fontSize: "0.7rem", marginTop: 3 }}>
                  📡 {s.broadcastLevel}
                </span>
              </a>
            ))}
          </div>
        </>
      )}

      <p style={{ marginTop: 12, color: "#263238", fontWeight: 500, fontSize: "0.82rem" }}>
        Tip: Your selected sims + admin broadcast sims both appear to your class students.
      </p>
    </Card>
  );
}

function TabRoster({ cls, students, setStudents, onSave, saving }) {
  const [roster, setRoster] = useState([]);             // master studentRoster
  const [rosterLoading, setRosterLoading] = useState(true);
  const [filter, setFilter] = useState("");             // search text
  const [batchFilter, setBatchFilter] = useState("All"); // All | C1 | C2
  const [semFilter, setSemFilter] = useState("All");
  const [branchFilter, setBranchFilter] = useState("All");
  const [manualEmail, setManualEmail] = useState("");
  const [bulk, setBulk] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "studentRoster"));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setRoster(list);
      } catch (e) { console.error("load roster", e); }
      finally { setRosterLoading(false); }
    })();
  }, []);

  const selected = new Set(students);
  const toggle = (email) => {
    const e = email.toLowerCase();
    if (selected.has(e)) setStudents(students.filter(x => x !== e));
    else setStudents([...students, e]);
  };

  const semsOf = r => (r.semesters && r.semesters.length ? r.semesters : (r.semester ? [r.semester] : []));
  const allSems = Array.from(new Set(roster.flatMap(semsOf))).sort().reverse();
  const allBranches = Array.from(new Set(roster.map(r => r.branch).filter(Boolean))).sort();
  const filtered = roster.filter(r => {
    if (batchFilter !== "All" && r.batch !== batchFilter) return false;
    if (semFilter !== "All" && !semsOf(r).includes(semFilter)) return false;
    if (branchFilter !== "All" && r.branch !== branchFilter) return false;
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (r.name || "").toLowerCase().includes(q) ||
           (r.email || "").toLowerCase().includes(q) ||
           (r.studentId || "").toLowerCase().includes(q);
  });

  const selectAllFiltered = () => {
    const next = new Set(students);
    filtered.forEach(r => r.email && next.add(r.email.toLowerCase()));
    setStudents(Array.from(next));
  };
  const clearAllFiltered = () => {
    const removeSet = new Set(filtered.map(r => r.email?.toLowerCase()).filter(Boolean));
    setStudents(students.filter(e => !removeSet.has(e)));
  };

  const addManual = () => {
    const e = manualEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) { alert("Enter a valid email"); return; }
    if (students.includes(e)) { alert("Already added"); return; }
    setStudents([...students, e]); setManualEmail("");
  };
  const addBulkManual = () => {
    const emails = bulk.split(/[,;\s\n]+/).map(s => s.trim().toLowerCase()).filter(s => s.includes("@"));
    const set = new Set(students);
    emails.forEach(e => set.add(e));
    setStudents(Array.from(set)); setBulk("");
  };

  return (
    <Card accent="#c62828">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ ...cardTitle, margin: 0, color: "#b71c1c", borderBottomColor: "#ffcdd2" }}>
          👥 Class Roster <span style={{ color: "#263238", fontWeight: 500, fontSize: "0.8rem", fontWeight: 400 }}>
            ({students.length} mapped {filtered.length !== roster.length ? `· showing ${filtered.length}/${roster.length}` : `· of ${roster.length}`})
          </span>
        </h3>
        <button onClick={onSave} disabled={saving} style={btnPrimary}>{saving ? "Saving…" : "💾 Save Roster"}</button>
      </div>

      {/* Filters */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginBottom: 12, alignItems: "center" }}>
        <input value={filter} onChange={e => setFilter(e.target.value)}
          placeholder="🔍 Search name / email / student ID" style={inputStyle} />
        <select value={batchFilter} onChange={e => setBatchFilter(e.target.value)} style={inputStyle}>
          <option value="All">All batches</option>
          <option value="C1">C1 only</option>
          <option value="C2">C2 only</option>
        </select>
        <select value={semFilter} onChange={e => setSemFilter(e.target.value)} style={inputStyle}>
          <option value="All">All semesters</option>
          {allSems.map(x => <option key={x} value={x}>{x}</option>)}
        </select>
        <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)} style={inputStyle}>
          <option value="All">All branches</option>
          {allBranches.map(x => <option key={x} value={x}>{x}</option>)}
        </select>
        <button onClick={selectAllFiltered} style={btnSecondary}>＋ Select shown</button>
        <button onClick={clearAllFiltered} style={btnSecondary}>− Clear shown</button>
      </div>

      {/* Master roster list */}
      {rosterLoading ? <p style={{ color: "#263238", fontWeight: 500 }}>⏳ Loading master student list…</p>
        : roster.length === 0 ? <Empty hint="No students in the master roster yet. Ask admin to upload it via Admin Panel." />
        : (
          <div style={{ maxHeight: 380, overflowY: "auto", borderRadius: 8, border: "1px solid #e0e4f3", marginBottom: 14 }}>
            {filtered.length === 0 && <p style={{ padding: 12, color: "#263238", fontWeight: 500, margin: 0 }}>No matches for current filter.</p>}
            {filtered.map(r => {
              const email = (r.email || "").toLowerCase();
              const on = selected.has(email);
              return (
                <label key={r.id} style={{
                  display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: 10, alignItems: "center",
                  padding: "8px 12px", borderBottom: "1px solid #f0f2f9",
                  background: on ? "#e8f1ff" : "#fff", cursor: "pointer", fontSize: "0.86rem"
                }}>
                  <input type="checkbox" checked={on} onChange={() => toggle(email)} />
                  <div>
                    <div style={{ fontWeight: 600, color: "#1a237e" }}>{r.name || "(no name)"}</div>
                    <div style={{ color: "#1c2833", fontWeight: 500, fontSize: "0.78rem" }}>{email}</div>
                  </div>
                  <span style={{
                    background: r.batch === "C1" ? "#e3f2fd" : "#fff3e0",
                    color: r.batch === "C1" ? "#1565c0" : "#e65100",
                    borderRadius: 12, padding: "2px 9px", fontSize: "0.72rem", fontWeight: 700
                  }}>{r.batch || "—"}</span>
                  <span style={{ color: "#263238", fontWeight: 500, fontSize: "0.74rem" }}>{r.studentId || ""}{r.branch ? ` · ${r.branch}` : ""}</span>
                </label>
              );
            })}
          </div>
        )}

      {/* Manual add (for students not yet in master roster) */}
      <details style={{ marginBottom: 6 }}>
        <summary style={{ cursor: "pointer", color: "#1a237e", fontWeight: 600, fontSize: "0.85rem" }}>
          ＋ Add a student NOT in the master roster (manual email entry)
        </summary>
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 8 }}>
            <input value={manualEmail} onChange={e => setManualEmail(e.target.value)}
              placeholder="student@gmail.com" style={inputStyle}
              onKeyDown={e => e.key === "Enter" && addManual()} />
            <button onClick={addManual} style={btnPrimary}>＋ Add</button>
          </div>
          <textarea value={bulk} onChange={e => setBulk(e.target.value)} rows={3}
            placeholder="Or paste multiple emails (space / comma / newline separated)"
            style={{ ...inputStyle, fontFamily: "monospace" }} />
          <button onClick={addBulkManual} style={{ ...btnPrimary, marginTop: 6 }}>＋ Add Bulk</button>
        </div>
      </details>

      {/* Manually-added students (those not matched to roster) */}
      {students.some(e => !roster.find(r => (r.email || "").toLowerCase() === e)) && (
        <div style={{ marginTop: 12 }}>
          <h4 style={{ color: "#1a237e", fontSize: "0.86rem", margin: "0 0 6px" }}>Manually added (not in master roster):</h4>
          {students.filter(e => !roster.find(r => (r.email || "").toLowerCase() === e)).map(e => (
            <div key={e} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "6px 12px", borderBottom: "1px solid #f0f2f9", fontSize: "0.86rem"
            }}>
              <span>📧 {e}</span>
              <button onClick={() => setStudents(students.filter(x => x !== e))} style={btnDanger}>✕</button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* =========================================================================
   Student-facing: My Classes
   Lists faculty classes the student is mapped into; lets them open one
   to see assigned reading, quizzes, and simulations (read-only).
   ========================================================================= */
export function StudentMyClasses({ user, setPage }) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!user?.email) return;
    (async () => {
      try {
        const email = user.email.toLowerCase();
        // 1. Look up the student's batch from the master roster (if present).
        let studentBatch = null;
        try {
          const rosterSnap = await getDoc(doc(db, "studentRoster", email));
          if (rosterSnap.exists()) studentBatch = (rosterSnap.data().batch || "").toUpperCase();
        } catch {}

        // 2. Classes where they're manually rostered
        const qManual = query(collection(db, "facultyClasses"),
                              where("studentEmails", "array-contains", email));
        // 3. Classes where batch == "Both" (everyone)
        const qBoth = query(collection(db, "facultyClasses"),
                            where("batch", "==", "Both"));
        // 4. Classes where batch matches student's batch
        const qBatch = studentBatch
          ? query(collection(db, "facultyClasses"), where("batch", "==", studentBatch))
          : null;

        const [manualSnap, bothSnap, batchSnap] = await Promise.all([
          getDocs(qManual),
          getDocs(qBoth),
          qBatch ? getDocs(qBatch) : Promise.resolve({ docs: [] }),
        ]);

        // Merge & dedupe by id
        const map = new Map();
        [...manualSnap.docs, ...bothSnap.docs, ...batchSnap.docs]
          .forEach(d => map.set(d.id, { id: d.id, ...d.data() }));

        setClasses(Array.from(map.values()).sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        ));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [user?.email]);

  if (active) return <StudentClassView cls={active} onBack={() => setActive(null)} user={user} setPage={setPage} />;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "20px 16px 40px" }}>
      <div style={{
        background: "linear-gradient(135deg,#1a237e 0%,#3949ab 100%)",
        borderRadius: 16, padding: "22px 26px", marginBottom: 22, color: "#fff",
        boxShadow: "0 4px 18px rgba(26,35,126,0.22)"
      }}>
        <h1 style={{ margin: "0 0 4px", fontSize: "1.5rem", fontWeight: 800 }}>📚 My Classes</h1>
        <p style={{ margin: 0, color: "#ffffff", fontWeight: 600, fontSize: "0.9rem" }}>
          Classes your faculty has enrolled you in. Open one to access its reading, quizzes, and simulations.
        </p>
      </div>

      {loading ? <p style={{ color: "#1a237e", fontWeight: 600 }}>⏳ Loading…</p>
        : classes.length === 0 ? (
        <div style={{ background: "#f5f7ff", borderRadius: 12, padding: "26px 18px", textAlign: "center", color: "#1c2833", fontWeight: 500 }}>
          <div style={{ fontSize: "2.2rem", marginBottom: 6 }}>🎒</div>
          <p style={{ margin: "0 0 4px", fontWeight: 700, color: "#1a237e" }}>No classes assigned yet</p>
          <p style={{ margin: 0, fontSize: "0.86rem" }}>Ask your faculty to add you to their class roster.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 14 }}>
          {classes.map(c => (
            <div key={c.id} onClick={() => setActive(c)} style={{
              background: "#fff", borderRadius: 14, padding: "18px 18px 14px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.07)", border: "1px solid #e8eaf6",
              cursor: "pointer", transition: "transform .12s,box-shadow .12s"
            }}
            onMouseOver={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 6px 18px rgba(26,35,126,0.15)"; }}
            onMouseOut={e =>  { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.07)"; }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <span style={{ fontSize: "1.8rem" }}>{c.icon || "📘"}</span>
                {c.batch && (
                  <span style={{
                    background: c.batch === "C1" ? "#e3f2fd" : c.batch === "C2" ? "#fff3e0" : "#e8f5e9",
                    color: c.batch === "C1" ? "#1565c0" : c.batch === "C2" ? "#e65100" : "#2e7d32",
                    borderRadius: 14, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700
                  }}>{c.batch}</span>
                )}
              </div>
              <h3 style={{ margin: "6px 0 4px", color: "#1a237e", fontSize: "1.02rem" }}>{c.name}</h3>
              <p style={{ margin: "0 0 6px", color: "#1c2833", fontWeight: 500, fontSize: "0.82rem" }}>{c.subject || "Physics"}</p>
              <p style={{ margin: 0, color: "#263238", fontWeight: 500, fontSize: "0.78rem" }}>👨‍🏫 {c.facultyName || c.facultyEmail}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StudentClassView({ cls, onBack, user, setPage }) {
  const [readings, setReadings] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [broadcastSims, setBroadcastSims] = useState([]);
  const [onlineClasses, setOnlineClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Online classes for this classroom: LIVE or UPCOMING (not ended).
  // Shared to specific classrooms (classIds) → only those; otherwise by batch.
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "onlineClasses"), orderBy("scheduledTime", "asc")));
        const now = Date.now();
        const classBatch = cls.batch || cls.level;
        setOnlineClasses(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => {
          const timeOk = c.isActive || (!c.endedAt &&
            new Date(c.scheduledTime).getTime() + (Number(c.duration) || 60) * 60000 >= now);
          if (!timeOk) return false;
          if (Array.isArray(c.classIds) && c.classIds.length > 0) return c.classIds.includes(cls.id);
          if (classBatch && classBatch !== "Both") return !c.batch || c.batch === "Both" || c.batch === classBatch;
          return true;
        }));
      } catch (e) { console.error("Online classes:", e); }
    })();
  }, [cls.id]);

  useEffect(() => {
    (async () => {
      try {
        const lvl = cls.level || cls.batch;
        const fQs = [
          getDocs(query(collection(db, "files"), where("facultyClassId", "==", cls.id))),
          getDocs(query(collection(db, "files"), where("broadcastLevel", "==", "All"))),
        ];
        const qQs = [
          getDocs(query(collection(db, "quizzes"), where("facultyClassId", "==", cls.id))),
          getDocs(query(collection(db, "quizzes"), where("broadcastLevel", "==", "All"))),
        ];
        const sQs = [
          getDocs(query(collection(db, "broadcastSimulations"), where("broadcastLevel", "==", "All"))),
        ];
        if (lvl) {
          fQs.push(getDocs(query(collection(db, "files"), where("broadcastLevel", "==", lvl))));
          qQs.push(getDocs(query(collection(db, "quizzes"), where("broadcastLevel", "==", lvl))));
          sQs.push(getDocs(query(collection(db, "broadcastSimulations"), where("broadcastLevel", "==", lvl))));
        }
        const [files, quizSnaps, simSnaps] = await Promise.all([Promise.all(fQs), Promise.all(qQs), Promise.all(sQs)]);
        const merge = (snaps) => {
          const m = new Map();
          snaps.forEach(s => s.docs.forEach(d => m.set(d.id, { id: d.id, ...d.data() })));
          return Array.from(m.values());
        };
        const allFiles = merge(files);
        setReadings(   smartSortFiles(allFiles.filter(f => (f.section || "Reading Materials") === "Reading Materials")));
        setAssignments(smartSortFiles(allFiles.filter(f => f.section === "Assignments")));
        setQuizzes(merge(quizSnaps));
        setBroadcastSims(merge(simSnaps));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [cls.id]);

  const simIds = cls.simulations || [];
  const sims = SIM_CATALOG.filter(s => simIds.includes(s.id));

  return (
    <div style={{ background: "#eef1f8", minHeight: "100vh", padding: "0 0 30px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto", padding: "20px 16px 40px" }}>
      <button onClick={onBack} style={{
        background: "none", border: "1.5px solid #c5cae9", color: "#1a237e",
        borderRadius: 8, padding: "6px 14px", cursor: "pointer", marginBottom: 14, fontWeight: 600
      }}>← My Classes</button>

      <div style={{
        background: "linear-gradient(135deg,#283593 0%,#3949ab 100%)",
        borderRadius: 14, padding: "22px 24px", marginBottom: 16, color: "#fff",
        display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap"
      }}>
        <div style={{ fontSize: "2.4rem" }}>{cls.icon || "📘"}</div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <h1 style={{ margin: "0 0 4px", fontSize: "1.3rem", fontWeight: 800 }}>{cls.name}</h1>
          <p style={{ margin: 0, color: "#ffffff", fontWeight: 600, fontSize: "0.85rem" }}>
            {cls.subject || "Physics"} · 👨‍🏫 {cls.facultyName || cls.facultyEmail}
          </p>
        </div>
      </div>

      {loading ? <p style={{ color: "#1a237e", fontWeight: 600 }}>⏳ Loading content…</p> : (
        <div style={{
          display: "grid",
          gridTemplateColumns: (cls.schedule && Object.keys(cls.schedule).length > 0) ? "minmax(0, 1fr) 300px" : "1fr",
          gap: 18, alignItems: "start",
        }}>
          <div style={{ minWidth: 0 }}>
            {/* Online classes */}
            {onlineClasses.length > 0 && (
              <Card accent="#d32f2f">
                <h3 style={{ ...cardTitle, color: "#b71c1c", borderBottomColor: "#ffcdd2" }}>🎓 Online Classes</h3>
                {onlineClasses.map(oc => (
                  <Row key={oc.id}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, color: oc.isActive ? "#c62828" : "#1a237e" }}>
                        {oc.isActive ? "🔴 LIVE: " : "🕒 UPCOMING: "}{oc.className}
                      </div>
                      <div style={{ color: "#263238", fontWeight: 500, fontSize: "0.8rem" }}>
                        📅 {new Date(oc.scheduledTime).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        {oc.description ? ` · ${oc.description}` : ""}
                      </div>
                    </div>
                    {oc.isActive ? (
                      <button onClick={() => window.open(`https://meet.google.com/${oc.roomId}`, "_blank")}
                        style={{ ...btnPrimary, background: "#c62828" }}>Join Class →</button>
                    ) : (
                      <span style={{ color: "#607d8b", fontSize: "0.78rem", fontWeight: 600 }}>Join opens when class starts</span>
                    )}
                  </Row>
                ))}
              </Card>
            )}

            {/* Reading */}
            <Card accent="#0288d1">
              <h3 style={{ ...cardTitle, color: "#01579b", borderBottomColor: "#b3e5fc" }}>📖 Reading Materials</h3>
              {readings.length === 0 ? <Empty hint="No reading materials yet." />
                : readings.map(r => (
                  <Row key={r.id}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <a href={r.url} target="_blank" rel="noreferrer"
                         style={{ color: "#1a237e", textDecoration: "none", fontWeight: 600 }}>📄 {r.name}</a>
                      {r.broadcastLevel && (
                        <span style={{ background: "#fff3e0", color: "#e65100", borderRadius: 10, padding: "1px 8px", fontSize: "0.7rem", fontWeight: 700 }}>📡 {r.broadcastLevel}</span>
                      )}
                    </div>
                    <a href={r.url} target="_blank" rel="noreferrer" style={{
                      background: "#1a237e", color: "#fff", borderRadius: 6, padding: "4px 12px",
                      fontWeight: 600, fontSize: "0.78rem", textDecoration: "none"
                    }}>Open ↗</a>
                  </Row>
                ))}
            </Card>

            {/* Assignments */}
            <Card accent="#e65100">
              <h3 style={{ ...cardTitle, color: "#bf360c", borderBottomColor: "#ffccbc" }}>📋 Assignments</h3>
              {assignments.length === 0 ? <Empty hint="No assignments yet." />
                : assignments.map(a => {
                  const overdue = a.dueDate && new Date(a.dueDate) < new Date();
                  return (
                    <Row key={a.id}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
                        <a href={a.url} target="_blank" rel="noreferrer" style={{ color: "#1a237e", textDecoration: "none", fontWeight: 700 }}>📋 {a.name}</a>
                        {a.dueDate && (
                          <span style={{ background: overdue ? "#ffebee" : "#fff3e0", color: overdue ? "#c62828" : "#e65100", borderRadius: 10, padding: "1px 8px", fontSize: "0.74rem", fontWeight: 700 }}>
                            📅 Due {new Date(a.dueDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}{overdue ? " — overdue" : ""}
                          </span>
                        )}
                        {a.broadcastLevel && (
                          <span style={{ background: "#fff3e0", color: "#e65100", borderRadius: 10, padding: "1px 8px", fontSize: "0.7rem", fontWeight: 700 }}>📡 {a.broadcastLevel}</span>
                        )}
                      </div>
                      <a href={a.url} target="_blank" rel="noreferrer" style={{
                        background: "#e65100", color: "#fff", borderRadius: 6, padding: "4px 12px",
                        fontWeight: 600, fontSize: "0.78rem", textDecoration: "none"
                      }}>Open ↗</a>
                    </Row>
                  );
                })}
            </Card>

            {/* Quizzes */}
            <Card accent="#2e7d32">
              <h3 style={{ ...cardTitle, color: "#1b5e20", borderBottomColor: "#c8e6c9" }}>📝 Quizzes</h3>
              {quizzes.length === 0 ? <Empty hint="No quizzes assigned yet." />
                : quizzes.map(q => (
                  <Row key={q.id}>
                    <div>
                      <div style={{ fontWeight: 700, color: "#1a237e" }}>{q.title}</div>
                      <div style={{ color: "#263238", fontWeight: 500, fontSize: "0.78rem" }}>
                        {(q.questions || []).length} questions · {q.timeLimit || 30} min
                      </div>
                    </div>
                    <button onClick={() => setPage && setPage("quiz")} style={btnPrimary}>Take Quiz →</button>
                  </Row>
                ))}
            </Card>

            {/* Simulations */}
            <Card accent="#6a1b9a">
              <h3 style={{ ...cardTitle, color: "#4a148c", borderBottomColor: "#e1bee7" }}>🔬 Simulations</h3>
              {sims.length === 0 && broadcastSims.length === 0 ? <Empty hint="No simulations assigned yet." />
                : (
                  <>
                    {sims.length > 0 && (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 10 }}>
                        {sims.map(s => (
                          <a key={s.id} href={s.url} target="_blank" rel="noreferrer" style={{
                            display: "block", padding: "12px 14px", background: "#fafbff",
                            border: "1.5px solid #e0e4f3", borderRadius: 10, textDecoration: "none",
                            color: "#1a237e", fontWeight: 600, fontSize: "0.88rem"
                          }}>{s.title} ↗</a>
                        ))}
                      </div>
                    )}
                    {broadcastSims.length > 0 && (
                      <>
                        <h4 style={{ color: "#4a148c", margin: "16px 0 8px", fontSize: "0.92rem", borderBottom: "2px solid #e1bee7", paddingBottom: 5 }}>
                          📡 Broadcast Simulations
                        </h4>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 10 }}>
                          {broadcastSims.map(s => (
                            <a key={s.id} href={s.url} target="_blank" rel="noreferrer" style={{
                              display: "block", padding: "12px 14px", background: "#fff3e0",
                              border: "1.5px solid #ffb74d", borderRadius: 10, textDecoration: "none",
                              color: "#e65100", fontWeight: 700, fontSize: "0.88rem"
                            }}>
                              🔬 {s.title}
                              <span style={{ display: "block", color: "#bf360c", fontWeight: 600, fontSize: "0.7rem", marginTop: 3 }}>📡 {s.broadcastLevel}</span>
                            </a>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
            </Card>

            {/* Discussion */}
            <TabDiscussion cls={cls} user={user} isFacultyOrAdmin={false} />
          </div>

          {cls.schedule && Object.keys(cls.schedule).length > 0 && (
            <SchedulePanel cls={cls} />
          )}
        </div>
      )}
    </div>
    </div>
  );
}

/* ------------------------------------------------------------------- */
/* tiny UI primitives                                                  */
/* ------------------------------------------------------------------- */
const inputStyle = {
  width: "100%", padding: "9px 11px", borderRadius: 8, border: "1.5px solid #c5cae9",
  fontSize: "0.9rem", outline: "none",
};
const btnPrimary = {
  background: "#1a237e", color: "#fff", border: "none", borderRadius: 8,
  padding: "9px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem",
};
const btnSecondary = {
  background: "#fff", color: "#1a237e", border: "1.5px solid #c5cae9",
  borderRadius: 8, padding: "9px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem",
};
const btnDanger = {
  background: "#fff", color: "#c62828", border: "1.5px solid #ef9a9a",
  borderRadius: 8, padding: "6px 12px", fontWeight: 600, cursor: "pointer", fontSize: "0.8rem",
};
const cardTitle = {
  color: "#0d1b5e", fontSize: "1.08rem", fontWeight: 900,
  marginBottom: 12, paddingBottom: 8,
  borderBottom: "2px solid #e8eaf6", letterSpacing: 0.2
};

function Card({ children, accent = "#1a237e" }) {
  return <div style={{
    background: "#fff",
    border: "1.5px solid #b8bff0",
    borderTop: `5px solid ${accent}`,
    borderRadius: 10,
    padding: 18,
    marginBottom: 16,
    boxShadow: "0 4px 14px rgba(26,35,126,0.12)"
  }}>{children}</div>;
}
function Row({ children }) {
  return <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "11px 14px", border: "1px solid #d6dcf0", borderRadius: 8,
    marginBottom: 7, background: "#f5f7ff",
    boxShadow: "0 1px 2px rgba(26,35,126,0.04)"
  }}>{children}</div>;
}
function Empty({ hint }) { return <p style={{ color: "#1c2833", fontWeight: 500, fontSize: "0.86rem", margin: "10px 0", fontWeight: 500 }}>— {hint}</p>; }
function Field({ label, children }) {
  return <div style={{ marginBottom: 12 }}>
    <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "#1a237e", marginBottom: 4 }}>{label}</label>
    {children}
  </div>;
}
function ModalActions({ children }) { return <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>{children}</div>; }
function Overlay({ onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 14, padding: 22, maxWidth: 460, width: "100%",
        boxShadow: "0 10px 40px rgba(0,0,0,0.25)"
      }}>{children}</div>
    </div>
  );
}
